import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import useFormWithSchema from "../../hooks/useFormWithSchema";
import * as Yup from "yup";
import { Controller } from "react-hook-form";
import ComboboxInput from "../inputs/ComboboxInput";
import Accordian from "../accordian";
import { useTranslation } from "react-i18next";
import { useGetAllAppointmentComments } from "../../hooks/visitor/query";
import { useLocation } from "react-router-dom";
import { SYSTEM_DETAIL_CODES } from "../../constants/common";
import { Button } from "../button";
import { useGetAllSystemDetailCodesDropdown } from "../../hooks/common/query";
import { mapToOptions } from "../../lib/functions";

interface SearchCommentsCardProps {
  setAllComments: Dispatch<SetStateAction<never[]>>;
  page: number;
  setPages: Dispatch<SetStateAction<number>>;
  search: string;
  setCount: Dispatch<SetStateAction<number>>;
  pageSize: number;
}

const SearchCommentsCardSchema = Yup.object({
  commentType: Yup.string(),
});

const initialState = {
  commentType: "",
};

const SearchCommentsCard = ({
  setAllComments,
  page,
  setPages,
  search,
  setCount,
  pageSize,
}: SearchCommentsCardProps) => {
  const {
    control,
    formState: { errors },
    reset,
    watch,
  } = useFormWithSchema(SearchCommentsCardSchema);

  const { t } = useTranslation();

  const params = useLocation();

  const [selectedKey, setSelectedKey] = useState<string>("");

  const searchParams = new URLSearchParams(params.search);

  const key = searchParams.get("ID");
  
  const { data: commentTypes } = useGetAllSystemDetailCodesDropdown({
    encryptedId: SYSTEM_DETAIL_CODES.COMMENT_TYPES,
  });

  const allCommentTypes = useMemo(
    () => mapToOptions("s_Desc_E", "s_Desc_A", "n_DetailedCode", commentTypes),
    [commentTypes]
  );

  const commentType = watch("commentType");

  const { data: comments } = useGetAllAppointmentComments({
    encryptedId: key || "",
    n_CommentType: Number(commentType) || 0,
    n_RoleID: 0,
    pageNumber: page,
    pageSize: pageSize,
    search: search,
  });

  useEffect(() => {
    if (comments) {
      setAllComments(comments.data || []);
      setPages(comments.pagination?.pages || 1);
      setCount(comments.pagination?.count || 0);
    }
  }, [params.pathname, setCount, setAllComments, setPages, comments]);

  const onClear = useCallback(() => {
    reset(initialState);
  }, [reset]);

  return (
    <div className="bg-white p-2 md:p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
      <Accordian
        id="1"
        selectedKey={selectedKey}
        onSelectionChange={setSelectedKey}
        title={
          <h1 className="text-xl md:text-2xl flex flex-1 -mt-2">
            {t("searchFilters")}
          </h1>
        }
      >
        <div className="flex flex-col gap-4">
          <form className="flex flex-col gap-4">
            <div className="grid sm:grid-cols-2 gap-2 lg:gap-4">
              <Controller
                name="commentType"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <ComboboxInput
                    options={allCommentTypes}
                    value={
                      allCommentTypes?.find((item) => item.value === value)
                        ?.labelEn || ""
                    }
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    label={t("commentType")}
                  />
                )}
              />
            </div>
          </form>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={onClear}
              className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
            >
              {t("clear")}
            </Button>
          </div>
        </div>
      </Accordian>
    </div>
  );
};

export default React.memo(SearchCommentsCard);
